import { createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { Cue_Entity } from "../../entities/cue";

import { filmsApi } from "../api/filmsApi";
import { scheduleInvalidation } from "../api/deferredInvalidation";

const cuesAdapter = createEntityAdapter<Cue_Entity>();
const initialState = cuesAdapter.getInitialState();

/** Everything a cue edit feeds into, refreshed once entry settles. */
const CUE_EDIT_TAGS = [
  "cues",
  "projectTasks",
  "tasks",
  "jobs",
  "budgets",
  "projects",
];

export const cueEndpoints = filmsApi.injectEndpoints({
  endpoints: (build) => ({
    getCues: build.query<
      EntityState<Cue_Entity>,
      Record<string, unknown> | void
    >({
      query: (args) => {
        return {
          url: `cues`,
          params: args,
        };
      },
      transformResponse: (responseData: Cue_Entity[]) => {
        return cuesAdapter.setAll(initialState, responseData);
      },
      providesTags: ["cues"],
    }),
    getCue: build.query<Cue_Entity, number>({
      query: (id) => `cues/${id}`,
      providesTags: (result, error, id) => [{ type: "cues", id }],
    }),
    createCue: build.mutation<Cue_Entity, Partial<Cue_Entity>>({
      query: (body) => ({
        method: "POST",
        body,
        url: `cues`,
      }),
      // A cue change is post-processed on the backend into tasks, jobs and
      // jobTasks and recomputes the project totals. Invalidate every derived
      // cache so the contract/Music Prep Summary panel (projectTasks), the
      // Orchestrators/Copyists tabs (jobs) and the Budget "Actual" (projects)
      // all refetch together instead of drifting out of sync.
      invalidatesTags: [
        "cues",
        "projectTasks",
        "tasks",
        "jobs",
        "budgets",
        "projects",
      ],
    }),
    updateCue: build.mutation<void, { id: number; body: Partial<Cue_Entity> }>({
      query: (args) => ({
        method: "PUT",
        body: args.body,
        url: `cues/${args.id}`,
      }),
      // Deliberately no invalidatesTags: a cue edit invalidates the query
      // feeding its own grid, so refreshing per cell rebuilt the rows mid-typing
      // and dropped everything typed after the first value. See
      // scheduleInvalidation.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // The grid reverts the cell and reports it; still refresh so the row
          // settles on what the server actually holds.
        }
        scheduleInvalidation(dispatch, CUE_EDIT_TAGS);
      },
    }),
    updateBatchCues: build.mutation<void, Partial<Cue_Entity>[]>({
      query: (args) => ({
        method: "PUT",
        body: args,
        url: `cues`,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          /* as above */
        }
        scheduleInvalidation(dispatch, CUE_EDIT_TAGS);
      },
    }),
    deleteCue: build.mutation<void, number>({
      query: (id) => ({
        method: "DELETE",
        url: `cues/${id}`,
      }),
      invalidatesTags: [
        "cues",
        "projectTasks",
        "tasks",
        "jobs",
        "budgets",
        "projects",
      ],
    }),
  }),
});

export const {
  useGetCueQuery,
  useGetCuesQuery,
  useUpdateBatchCuesMutation,
  useCreateCueMutation,
  useDeleteCueMutation,
  useUpdateCueMutation,
} = cueEndpoints;

export default cueEndpoints;
